import {memo, useMemo, useState, useCallback} from 'react';
import {ValidationError} from '@api/lib/payrollProcessing/types';
import {Card, CardContent} from '@common-web/components/ui/card';
import {Badge} from '@common-web/components/ui/badge';
import {Button} from '@common-web/components/ui/button';
import {
  AlertCircle,
  AlertTriangle,
  User,
  Hash,
  FileText,
  ChevronRight,
  X,
  Download,
} from 'lucide-react';
import clsx from 'clsx';
import {toTitleCase} from '@common/lib/utils';

type ErrorGroup = {
  type: string;
  typeLabel: string;
  level: 'HIGH' | 'LOW';
  errors: (ValidationError & {person: string})[];
  uniqueFields: string[];
  sampleMessages: string[];
};

const ERROR_TYPE_ICONS: Record<string, React.ReactNode> = {
  MISSING_REQUIRED_VALUE: <AlertCircle className='h-4 w-4' />,
  INVALID_DATE_FORMAT: <AlertTriangle className='h-4 w-4' />,
  INVALID_NUMBER_FORMAT: <AlertTriangle className='h-4 w-4' />,
  DUPLICATE_EMPLOYEE: <User className='h-4 w-4' />,
  INVALID_SSN: <Hash className='h-4 w-4' />,
  DEFAULT: <FileText className='h-4 w-4' />,
};

const getErrorIcon = (type: string) => {
  return ERROR_TYPE_ICONS[type] || ERROR_TYPE_ICONS.DEFAULT;
};

export const RowIssuesCardView = memo(
  ({
    errors,
    onExportErrorReportCsv,
  }: {
    errors: ValidationError[];
    onExportErrorReportCsv: () => void;
  }) => {
    const [expandedType, setExpandedType] = useState<string | null>(null);

    const errorGroups = useMemo(() => {
      const groups: Record<string, ErrorGroup> = {};

      for (const error of errors) {
        const firstName = error.rowContext?.FIRST_NAME || '';
        const lastName = error.rowContext?.LAST_NAME || '';
        const employeeId = error.rowContext?.EMPLOYEE_ID || '';
        const name = [firstName, lastName].filter(Boolean).join(' ').trim();
        const person = name || employeeId || '';

        if (!groups[error.type]) {
          groups[error.type] = {
            type: error.type,
            typeLabel: toTitleCase(error.type.replace(/_/g, ' ')),
            level: error.level,
            errors: [],
            uniqueFields: [],
            sampleMessages: [],
          };
        }

        groups[error.type].errors.push({...error, person});

        if (error.level === 'HIGH') {
          groups[error.type].level = 'HIGH';
        }

        if (error.fieldName && !groups[error.type].uniqueFields.includes(error.fieldName)) {
          groups[error.type].uniqueFields.push(error.fieldName);
        }

        if (
          error.message &&
          groups[error.type].sampleMessages.length < 2 &&
          !groups[error.type].sampleMessages.includes(error.message)
        ) {
          groups[error.type].sampleMessages.push(error.message);
        }
      }

      return Object.values(groups).sort((a, b) => {
        if (a.level !== b.level) return a.level === 'HIGH' ? -1 : 1;
        return b.errors.length - a.errors.length;
      });
    }, [errors]);

    const toggleExpanded = useCallback((type: string) => {
      setExpandedType((prev) => (prev === type ? null : type));
    }, []);

    const expandedErrorGroup = expandedType
      ? errorGroups.find((g) => g.type === expandedType)
      : null;

    return (
      <div className='space-y-4'>
        <div className='flex items-center justify-end'>
          <Button
            variant='outline'
            size='sm'
            onClick={onExportErrorReportCsv}
            className='py-5 px-3 text-xs'
          >
            <Download className='h-4 w-4 mr-2' /> Export row issues as CSV
          </Button>
        </div>
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
          {errorGroups.map((group) => (
            <ErrorGroupCard
              key={group.type}
              group={group}
              isExpanded={expandedType === group.type}
              onToggle={() => toggleExpanded(group.type)}
            />
          ))}
        </div>
        {!!expandedErrorGroup && (
          <ExpandedErrorDetail group={expandedErrorGroup} onClose={() => setExpandedType(null)} />
        )}
      </div>
    );
  },
);

const ErrorGroupCard = memo(
  ({
    group,
    isExpanded,
    onToggle,
  }: {
    group: ErrorGroup;
    isExpanded: boolean;
    onToggle: () => void;
  }) => {
    const isError = group.level === 'HIGH';

    return (
      <Card
        className={clsx(
          'cursor-pointer transition-all hover:shadow-md',
          isExpanded && 'ring-2',
          isError
            ? isExpanded
              ? 'ring-red-300 border-red-200'
              : 'border-gray-200 hover:border-red-200'
            : isExpanded
              ? 'ring-amber/50 border-amber/50'
              : 'border-gray-200 hover:border-amber/50',
        )}
        onClick={onToggle}
      >
        <CardContent className='p-4'>
          <div className='flex items-start gap-3'>
            <div
              className={clsx(
                'shrink-0 w-10 h-10 rounded-lg flex items-center justify-center',
                isError ? 'bg-red-50 text-red-500' : 'bg-amber/10 text-amber',
              )}
            >
              {getErrorIcon(group.type)}
            </div>
            <div className='flex-1 min-w-0'>
              <div className='flex items-center gap-2 mb-1'>
                <Badge
                  className={clsx(
                    'text-[10px] font-semibold border-0 px-1.5 py-0',
                    isError
                      ? 'bg-red-500 hover:bg-red-500 text-white'
                      : 'bg-amber hover:bg-amber text-white',
                  )}
                >
                  {isError ? 'ERROR' : 'WARN'}
                </Badge>
              </div>
              <h3
                className='font-medium text-internal-brand-navy text-sm leading-snug mb-1 line-clamp-3'
                title={group.typeLabel}
              >
                {group.typeLabel}
              </h3>
              <div className='flex items-center gap-2'>
                <span className='text-2xl font-bold text-internal-brand-navy'>
                  {group.errors.length}
                </span>
                <span className='text-xs text-internal-brand-gray'>
                  row{group.errors.length !== 1 ? 's' : ''} affected
                </span>
              </div>
              {group.uniqueFields.length > 0 && (
                <div className='mt-2 flex flex-wrap gap-1'>
                  {group.uniqueFields.slice(0, 3).map((field) => (
                    <span
                      key={field}
                      className='text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-sm'
                    >
                      {field.replace(/_/g, ' ')}
                    </span>
                  ))}
                  {group.uniqueFields.length > 3 && (
                    <span className='text-[10px] text-gray-400'>
                      +{group.uniqueFields.length - 3}
                    </span>
                  )}
                </div>
              )}
            </div>
            <ChevronRight
              className={clsx(
                'h-5 w-5 text-internal-brand-gray shrink-0 transition-transform',
                isExpanded && 'rotate-90',
              )}
            />
          </div>
        </CardContent>
      </Card>
    );
  },
);

const ErrorDescriptionSection = memo(({group}: {group: ErrorGroup}) => {
  const isDuplicateError = group.type === 'DUPLICATE_EMPLOYEE';

  if (isDuplicateError) {
    const matchingFields = group.uniqueFields[0]?.split(',').map((f) => f.trim()) || [];

    return (
      <div className='px-4 py-4 bg-gradient-to-b from-gray-50 to-white border-b border-gray-100'>
        <div className='flex items-start gap-3'>
          <div className='shrink-0 w-8 h-8 rounded-full bg-amber/10 flex items-center justify-center'>
            <User className='h-4 w-4 text-amber' />
          </div>
          <div className='flex-1 min-w-0'>
            <p className='text-sm text-gray-800 leading-relaxed'>
              {group.errors.length} row{group.errors.length !== 1 ? 's' : ''} contain duplicate
              employee records that will be <strong>skipped during processing</strong>.
            </p>
            <div className='mt-3 flex flex-wrap items-center gap-2'>
              <span className='text-xs font-medium text-gray-600'>Matched on:</span>
              {matchingFields.map((field) => (
                <span
                  key={field}
                  className='text-xs bg-amber/10 text-amber-800 border border-amber/20 px-2 py-0.5 rounded-full'
                >
                  {field.replace(/_/g, ' ')}
                </span>
              ))}
            </div>
          </div>
        </div>
        <div className='mt-4 flex items-center gap-2 p-2.5 bg-green-50/80 border border-green-200 rounded-lg'>
          <div className='shrink-0 w-5 h-5 rounded-full bg-green-100 flex items-center justify-center'>
            <span className='text-green-600 text-xs font-bold'>✓</span>
          </div>
          <p className='text-xs text-green-800'>
            For each duplicate set, the row with <strong>highest gross wages</strong> is
            automatically kept. See the green badge on each row below.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className='px-4 py-4 bg-gradient-to-b from-gray-50 to-white border-b border-gray-100'>
      <div className='space-y-2'>
        {group.sampleMessages.map((msg, i) => (
          <p key={i} className='text-sm text-gray-800 leading-relaxed'>
            {msg}
          </p>
        ))}
      </div>
      {group.uniqueFields.length > 0 && (
        <div className='mt-3 flex flex-wrap items-center gap-2'>
          <span className='text-xs font-medium text-gray-600'>Affected field(s):</span>
          {group.uniqueFields.map((field) => (
            <span
              key={field}
              className='text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full'
            >
              {field.replace(/_/g, ' ')}
            </span>
          ))}
        </div>
      )}
    </div>
  );
});

const ErrorRowItem = memo(
  ({error, group}: {error: ValidationError & {person: string}; group: ErrorGroup}) => {
    const isDuplicateError = group.type === 'DUPLICATE_EMPLOYEE';

    const duplicateMatchValues = useMemo(() => {
      if (!isDuplicateError || !error.value) return null;
      const parts = error.value.split(', ').map((part) => {
        const [label, ...valueParts] = part.split(': ');
        return {label: label?.trim(), value: valueParts.join(': ')?.trim()};
      });
      return parts.filter((p) => p.label && p.value);
    }, [isDuplicateError, error.value]);

    return (
      <div className='px-4 py-3 hover:bg-gray-50/80 transition-colors'>
        <div className='flex items-center gap-3 flex-wrap'>
          <div className='flex items-center gap-1.5'>
            <span className='text-xs text-gray-500'>Row</span>
            <span className='font-mono text-sm font-semibold text-internal-brand-navy'>
              {error.rowNumber ?? '-'}
            </span>
            {!!error.rowFileId && (
              <span className='text-[10px] text-gray-500 bg-gray-100 font-medium px-1.5 py-0.5 rounded-sm'>
                {error.rowFileId}
              </span>
            )}
          </div>
          {isDuplicateError && error.rowContext?.keptRowNumber && (
            <span className='text-[10px] font-medium text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full'>
              → Row {error.rowContext.keptRowNumber} kept
            </span>
          )}
          {!!error.person && (
            <div
              className='flex items-center gap-1.5 px-2 py-1 bg-gray-50 rounded-sm'
              title={error.person}
            >
              <User className='h-3.5 w-3.5 text-gray-400' />
              <span className='text-sm text-gray-700 truncate max-w-[180px]'>{error.person}</span>
            </div>
          )}
        </div>
        {isDuplicateError && duplicateMatchValues && duplicateMatchValues.length > 0 && (
          <div className='mt-2 ml-0 flex flex-wrap items-center gap-x-4 gap-y-1'>
            <span className='text-[10px] uppercase font-semibold text-gray-400 tracking-wide'>
              Duplicate Key
            </span>
            {duplicateMatchValues.map(({label, value}, i) => (
              <div key={i} className='flex items-center gap-1'>
                <span className='text-[10px] text-gray-500'>{label}:</span>
                <span className='text-xs font-mono text-gray-700'>{value}</span>
              </div>
            ))}
          </div>
        )}
        {!isDuplicateError && !!error.value && (
          <div className='mt-2 flex items-center gap-2'>
            <span className='text-xs text-gray-500'>Value:</span>
            <span
              className='font-mono text-xs text-gray-700 bg-gray-50 px-2 py-0.5 rounded-sm'
              title={error.value}
            >
              {error.value.length > 80 ? error.value.slice(0, 80) + '...' : error.value}
            </span>
          </div>
        )}
        {!isDuplicateError && group.uniqueFields.length > 1 && !!error.fieldName && (
          <div className='mt-1.5 flex items-center gap-1.5'>
            <span className='text-[10px] text-gray-500'>Field:</span>
            <span className='text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-sm'>
              {error.fieldName.replace(/_/g, ' ')}
            </span>
          </div>
        )}
      </div>
    );
  },
);

const ExpandedErrorDetail = memo(({group, onClose}: {group: ErrorGroup; onClose: () => void}) => {
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const totalPages = Math.ceil(group.errors.length / pageSize);
  const paginatedErrors = group.errors.slice((page - 1) * pageSize, page * pageSize);
  const isError = group.level === 'HIGH';

  return (
    <Card
      className={clsx(
        'mt-4 border overflow-hidden',
        isError ? 'border-red-200' : 'border-amber/30',
      )}
    >
      <div
        className={clsx(
          'px-4 py-3 border-b flex items-center justify-between',
          isError ? 'bg-red-50/50 border-red-100' : 'bg-amber/5 border-amber/20',
        )}
      >
        <div className='flex items-center gap-3'>
          <div
            className={clsx(
              'w-8 h-8 rounded-lg flex items-center justify-center',
              isError ? 'bg-red-100 text-red-500' : 'bg-amber/10 text-amber',
            )}
          >
            {getErrorIcon(group.type)}
          </div>
          <div>
            <h3 className='font-semibold text-sm text-internal-brand-navy'>{group.typeLabel}</h3>
            <p className='text-[11px] text-gray-600'>
              {group.errors.length} row{group.errors.length !== 1 ? 's' : ''} will be skipped
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className='p-1.5 rounded-lg hover:bg-white/80 transition-colors'
          aria-label='Close'
        >
          <X className='h-4 w-4 text-gray-500' />
        </button>
      </div>
      <CardContent className='p-0'>
        <ErrorDescriptionSection group={group} />
        <div className='divide-y divide-gray-100 max-h-[400px] overflow-y-auto'>
          {paginatedErrors.map((error, idx) => (
            <ErrorRowItem key={idx} error={error} group={group} />
          ))}
        </div>
        {totalPages > 1 && (
          <div className='px-4 py-3 border-t border-gray-100 flex items-center justify-between bg-gray-50'>
            <span className='text-xs text-gray-600'>
              Showing {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, group.errors.length)}{' '}
              of {group.errors.length}
            </span>
            <div className='flex items-center gap-2'>
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className={clsx(
                  'px-3 py-1 text-xs font-medium rounded-sm',
                  page === 1
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-internal-brand-blue hover:bg-internal-brand-blue/10',
                )}
              >
                Previous
              </button>
              <span className='text-xs text-gray-600'>
                {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className={clsx(
                  'px-3 py-1 text-xs font-medium rounded-sm',
                  page === totalPages
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-internal-brand-blue hover:bg-internal-brand-blue/10',
                )}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
});
